window.globalState = {
    isAuth: window.laravelConfig.isAuth,
    activeModal: null,
    modalConfirmTarget: null,
    modalPayload: null,
    notifications: [],

    _afterLogin: null,
    _afterCodeConfirm: null,

    updateAuth(value) {
        this.isAuth = value;

        if (window.authChangeCallbacks) {
            window.authChangeCallbacks.forEach(callback => callback(value));
        }
    },

    onAuthChange(callback) {
        if (!window.authChangeCallbacks) window.authChangeCallbacks = [];
        window.authChangeCallbacks.push(callback);
        return () => {
            window.authChangeCallbacks =
                window.authChangeCallbacks.filter(cb => cb !== callback);
        };
    },

    openModal(modalName, payload = null) {
        this.activeModal = modalName;
        this.modalPayload = payload;
        document.body.style.overflow = 'hidden';

        if (window.modalChangeCallbacks) {
            window.modalChangeCallbacks.forEach(callback =>
                callback(modalName, payload)
            );
        }
    },

    closeModal() {
        this.activeModal = null;
        this.modalPayload = null;
        document.body.style.overflow = '';

        if (window.modalChangeCallbacks) {
            window.modalChangeCallbacks.forEach(callback => callback(null));
        }
    },

    onModalChange(callback) {
        if (!window.modalChangeCallbacks) window.modalChangeCallbacks = [];
        window.modalChangeCallbacks.push(callback);
        return () => {
            window.modalChangeCallbacks =
                window.modalChangeCallbacks.filter(cb => cb !== callback);
        };
    },

    addNotification(message, type = 'info', duration = 3000) {
        const notification = {
            id: Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            message,
            type,
            duration
        };

        this.notifications = [...this.notifications, notification];

        if (window.notificationChangeCallbacks) {
            window.notificationChangeCallbacks.forEach(callback =>
                callback([...this.notifications])
            );
        }

        if (duration > 0) {
            setTimeout(() => {
                this.removeNotification(notification.id);
            }, duration);
        }

        return notification.id;
    },

    removeNotification(id) {
        this.notifications = this.notifications.filter(n => n.id !== id);

        if (window.notificationChangeCallbacks) {
            window.notificationChangeCallbacks.forEach(callback =>
                callback([...this.notifications])
            );
        }
    },

    onNotificationChange(callback) {
        if (!window.notificationChangeCallbacks) window.notificationChangeCallbacks = [];
        window.notificationChangeCallbacks.push(callback);
        return () => {
            window.notificationChangeCallbacks =
                window.notificationChangeCallbacks.filter(cb => cb !== callback);
        };
    },

    cart: {
        products: [],
        total: 0,
        isLoaded: false,
        loading: false,

        notify() {
            if (window.cartChangeCallbacks) {
                window.cartChangeCallbacks.forEach(cb =>
                    cb({
                        products: this.products,
                        total: this.total
                    })
                );
            }
        }
    },

    onCartChange(callback) {
        if (!window.cartChangeCallbacks) window.cartChangeCallbacks = [];
        window.cartChangeCallbacks.push(callback);
        return () => {
            window.cartChangeCallbacks =
                window.cartChangeCallbacks.filter(cb => cb !== callback);
        };
    },

    async loadCart() {
        const cart = this.cart;

        if (cart.loading) {
            return { products: cart.products, total: cart.total };
        }

        cart.loading = true;

        try {
            /* ============================
             * 1️⃣ Инициализация из localStorage
             * ============================ */
            const storedCart = JSON.parse(localStorage.getItem("cart")) || [];

            if (storedCart.length > 0) {
                cart.products = storedCart.map(i => ({
                    id: i.product_id,
                    price: i.price,
                    cartQuantity: i.quantity,
                    isCart: i.quantity > 0
                }));

                cart.total = storedCart.reduce(
                    (sum, i) => sum + (i.price * i.quantity),
                    0
                );

                cart.notify(); // 🔥 цена появляется сразу
            }

            /* ============================
             * 2️⃣ Сервер (если авторизован)
             * ============================ */
            if (this.isAuth) {
                const resp = await fetch(window.appConfig.routes.getUserCartItems, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-TOKEN": window.appConfig.csrfToken
                    }
                });

                const serverProducts = await resp.json();

                if (Array.isArray(serverProducts)) {
                    cart.products = serverProducts;
                    cart.total = serverProducts.reduce(
                        (sum, p) => sum + (p.price * p.cartQuantity),
                        0
                    );

                    // минимальный cart в localStorage
                    localStorage.setItem(
                        "cart",
                        JSON.stringify(
                            serverProducts.map(p => ({
                                product_id: p.id,
                                quantity: p.cartQuantity,
                                price: p.price
                            }))
                        )
                    );

                    cart.notify(); // 🔥 сервер подтвердил
                }
            }

            return { products: cart.products, total: cart.total };

        } catch (e) {
            console.error("loadCart error:", e);
            return { products: cart.products, total: cart.total };
        } finally {
            cart.loading = false;
        }
    },

    clearCart() {
        this.cart.products = [];
        this.cart.total = 0;
        this.cart.isLoaded = true;

        localStorage.removeItem('cartProducts');
        localStorage.removeItem('cartTotal');

        this.cart.notify();
    },

    slugify(str) {
        return str
            .toLowerCase()
            .trim()
            .replace(/[\s]+/g, "-")
            .replace(/[^a-z0-9-]/g, "");
    },

    formatPhone(raw) {
        if (!raw) return '';

        let digits = raw.replace(/\D/g, '');

        if (/^[78]/.test(digits)) {
            digits = digits.substring(1);
        }

        digits = digits.substring(0, 10);

        let result = '+7';
        if (digits.length > 0) result += ' ' + digits.substring(0, 3);
        if (digits.length > 3) result += ' ' + digits.substring(3, 6);
        if (digits.length > 6) result += ' ' + digits.substring(6, 8);
        if (digits.length > 8) result += ' ' + digits.substring(8, 10);

        return result;
    },

    paginationPages( pagesCount, currentPage ) {
        const total = pagesCount;
        const current = currentPage;
        const maxVisible = 5;

        if (total <= maxVisible) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }

        let start = current - Math.floor(maxVisible / 2);
        let end = current + Math.floor(maxVisible / 2);

        if (start < 1) {
            start = 1;
            end = maxVisible;
        }

        if (end > total) {
            end = total;
            start = total - maxVisible + 1;
        }

        return Array.from(
            { length: end - start + 1 },
            (_, i) => start + i
        );
    },

    updateSingleCartProduct(product) {
        const cart = this.cart;
        const index = cart.products.findIndex(p => p.id === product.id);

        if (product.isCart && product.cartQuantity > 0) {
            if (index >= 0) {
                cart.products[index] = product;
            } else {
                cart.products.push(product);
            }
        } else {
            if (index >= 0) cart.products.splice(index, 1);
        }

        cart.total = cart.products.reduce(
            (sum, p) => sum + (p.isCart ? p.price * p.cartQuantity : 0),
            0
        );

        cart.isLoaded = true;

        // ✅ MINIMAL localStorage
        const minimalCart = cart.products.map(p => ({
            product_id: p.id,
            quantity: p.cartQuantity,
            price: p.price
        }));

        localStorage.setItem("cart", JSON.stringify(minimalCart));
        localStorage.setItem("cartTotal", String(cart.total));

        cart.notify();
    },

    formatPrice(price) {
        if (price == null) return "Цена по запросу";
        return new Intl.NumberFormat("ru-RU", {
            style: "currency",
            currency: "RUB",
            minimumFractionDigits: 0
        }).format(price);
    },
};

window.globalState.onAuthChange((isAuth) => {
    if (isAuth && window.cartHelpers) {
        window.cartHelpers.syncCartOnLogin();

        window.globalState.cart.isLoaded = false;
        window.globalState.loadCart();
    }
});